/** Turn `partially_paid` into `Partially paid` when no label is declared. */
export function humanise(value: string): string {
  const spaced = value.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * The tint a value's pill is drawn in (#555) — a database property tag, so a
 * column of statuses is read by colour before it is read by word.
 *
 * By meaning where the word has one: done is green, waiting is yellow, moving
 * is blue, partly-there is orange, stopped is red, and not-yet-anything is
 * grey. That order of tests matters — "unfulfilled" and "unpaid" have to be
 * caught as waiting before "fulfilled" and "paid" are caught as done.
 *
 * A value with no such meaning — a channel, a contact type, a delivery
 * method — gets one of the four hues that say nothing about good or bad,
 * chosen from the value itself so the same word is the same colour on every
 * screen and after every reload. Never green or red by accident: a colour
 * that looks like a verdict and is not one is worse than no colour.
 *
 * Colour only. The label is still the whole of the meaning, which is what
 * keeps this readable to someone who cannot tell the tints apart.
 */
const TINT_BY_MEANING: [RegExp, string][] = [
  // A priority scale first, as whole words: "low" alone is the quiet end of
  // one, where "low_stock" further down is something waiting to be dealt with.
  [/^(urgent|critical)$/, "bg-tint-red text-tint-red-foreground"],
  [/^high$/, "bg-tint-orange text-tint-orange-foreground"],
  [/^(medium|normal)$/, "bg-tint-yellow text-tint-yellow-foreground"],
  [/^low$/, "bg-tint-gray text-tint-gray-foreground"],
  // Not started is grey, so it cannot be mistaken for the blue of under way.
  [/^(to_?do|backlog|not_started)$/, "bg-tint-gray text-tint-gray-foreground"],
  [
    /archiv|inactive|disabled|closed|^none$|unknown|^other$/,
    "bg-tint-gray text-tint-gray-foreground",
  ],
  [
    /^(un|not_|no_|low)|incomplete|invit|pending|awaiting|waiting|draft|scheduled|queued|on_hold/,
    "bg-tint-yellow text-tint-yellow-foreground",
  ],
  [
    /cancel|void|fail|reject|error|overdue|lost|blocked|out_of_stock|expired|declin|missing/,
    "bg-tint-red text-tint-red-foreground",
  ],
  [
    /partial|return|refund|restock|attempt|exception|delay/,
    "bg-tint-orange text-tint-orange-foreground",
  ],
  [
    /transit|progress|processing|shipped|dispatch|out_for|picked|packed|authori[sz]ed|review|syncing|running|^open$/,
    "bg-tint-blue text-tint-blue-foreground",
  ],
  [
    /paid|fulfilled|deliver|complete|done|active|success|received|approved|accepted|resolved|in_stock|synced|published|issued/,
    "bg-tint-green text-tint-green-foreground",
  ],
];

const NEUTRAL_TINTS = [
  "bg-tint-blue text-tint-blue-foreground",
  "bg-tint-purple text-tint-purple-foreground",
  "bg-tint-pink text-tint-pink-foreground",
  "bg-tint-brown text-tint-brown-foreground",
];

export function tintFor(value: string): string {
  const key = value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  for (const [pattern, tint] of TINT_BY_MEANING) {
    if (pattern.test(key)) return tint;
  }
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 31 + key.charCodeAt(index)) >>> 0;
  }
  return NEUTRAL_TINTS[hash % NEUTRAL_TINTS.length] ?? "";
}
